import React from 'react';
import { Page } from '../types';
import { SERVICES } from '../constants';

interface ServiceDetailProps {
  type: 'cac' | 'tax' | 'bookkeeping' | 'post-inc' | 'advisory';
  setPage: (page: Page) => void;
}

const ServiceDetail: React.FC<ServiceDetailProps> = ({ type, setPage }) => {
  const serviceInfo = SERVICES.find(s => {
    if (type === 'cac') return s.link === 'cac-registration';
    if (type === 'tax') return s.link === 'tax-compliance';
    if (type === 'bookkeeping') return s.link === 'bookkeeping';
    if (type === 'post-inc') return s.link === 'post-inc';
    if (type === 'advisory') return s.link === 'advisory';
    return false;
  });

  const content = {
    cac: {
      title: "CAC Registration",
      subtitle: "The foundation of your business starts here.",
      description: "We handle everything from name reservation to the final certificate. Whether you're a solo freelancer or a scaling startup, we ensure your legal structure is solid.",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=1000",
      logo: "https://bor.cac.gov.ng/static/media/Test.e531c24e977be870325a.png",
      steps: [
        { title: "Name Search & Reservation", desc: "We find two available names and reserve the best one for 60 days." },
        { title: "Documentation", desc: "We prepare the MEMART, share capital structure, and director details." },
        { title: "Payment & Filing", desc: "We pay the stamp duties and filing fees to the CAC portal." },
        { title: "Certification", desc: "Receive your Digital Certificate, Status Report, and TIN within 14 days." }
      ],
      requirements: [
        "Proposed Business Names",
        "Valid ID (NIN, Driver's License, or Passport)",
        "Passport Photographs",
        "Business Address in Nigeria",
        "Email and Phone Number of Directors"
      ]
    },
    tax: {
      title: "Tax & Compliance",
      subtitle: "Stay worry-free with professional tax management.",
      description: "Taxes in Nigeria can be complex. We manage your VAT, CIT, PAYE, and Statutory Remittances to ensure you stay in the good books of the FIRS and LIRS.",
      image: "https://theforage.wpengine.com/wp-content/uploads/2022/08/investment-banking-hours.png",
      steps: [
        { title: "TIN Activation", desc: "We activate your Tax Identification Number on the FIRS portal." },
        { title: "VAT Registration", desc: "Registration for Value Added Tax and filing of monthly returns." },
        { title: "Annual Returns", desc: "Managing your year-end tax returns and CAC annual returns." },
        { title: "Tax Clearance", desc: "Securing your TCC which is essential for government contracts and visas." }
      ],
      requirements: [
        "Certificate of Incorporation",
        "TIN Letter",
        "Past 6 months Bank Statements (for older businesses)",
        "Employee details for PAYE",
        "Current Business Status Report"
      ]
    },
    bookkeeping: {
      title: "Bookkeeping & Accounting",
      subtitle: "Crystal clear financials for smarter decisions.",
      description: "Stop guessing your margins. Our chartered accountants maintain your books, track every expense, and provide monthly management reports that investors love.",
      image: "https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?auto=format&fit=crop&q=80&w=1000",
      steps: [
        { title: "Setup & Integration", desc: "We set up your chart of accounts on cloud software (Xero/Quickbooks)." },
        { title: "Daily Entry", desc: "Recording of all sales, purchases, and bank transactions daily." },
        { title: "Reconciliation", desc: "Monthly matching of bank statements with internal records." },
        { title: "Reporting", desc: "Delivery of P&L, Balance Sheet, and Cash Flow statements by the 5th of every month." }
      ],
      requirements: [
        "Business Bank Statements",
        "Access to Invoices & Receipts",
        "Payroll data (if applicable)",
        "Previous year's audit reports",
        "Inventory list (for retail)"
      ]
    },
    'post-inc': {
      title: "Post-Incorporation",
      subtitle: "Evolve your business without the paperwork headache.",
      description: "As your startup grows, your records must too. We handle share capital increases, changes in directors, and mandatory annual returns to keep your status 'Active'.",
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=1000",
      steps: [
        { title: "Status Review", desc: "Audit of your current CAC status and identification of missing filings." },
        { title: "Resolution Drafting", desc: "Legal preparation of board resolutions for the intended changes." },
        { title: "Filing & Payment", desc: "Submission of updated records to the CAC portal with paid fees." },
        { title: "Confirmation", desc: "Receipt of updated Status Report and Certified True Copies." }
      ],
      requirements: [
        "Current Status Report",
        "Board Resolution signed by Directors",
        "Details of new Directors/Shareholders",
        "Copy of Certificate of Incorporation",
        "Evidence of last Annual Return payment"
      ]
    },
    advisory: {
      title: "Business Advisory",
      subtitle: "Strategic insights for a competitive edge.",
      description: "Beyond compliance, we provide the financial intelligence needed to scale. From pitch deck reviews to regulatory moat strategy, we are your growth partners.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1000",
      steps: [
        { title: "Diagnostic", desc: "A deep dive into your current financial health and business model." },
        { title: "Strategy Session", desc: "1-on-1 session with a senior partner to map out growth levers." },
        { title: "Implementation", desc: "Hands-on support in restructuring or optimizing for scale." },
        { title: "Ongoing Review", desc: "Quarterly check-ins to ensure your business stays on track." }
      ],
      requirements: [
        "Current Business Model Canvas",
        "6-month historical financials",
        "Growth targets for next 12 months",
        "Market size data (TAM/SAM/SOM)",
        "Key challenges summary"
      ]
    }
  }[type];

  const handleStart = () => {
    // Save current selection for booking context
    localStorage.setItem('fintrack_selected_service', JSON.stringify({
        title: content.title,
        price: serviceInfo?.price || '₦0',
        fee: serviceInfo?.fee || ''
    }));
    setPage('booking');
  };

  return (
    <div className="pt-32 pb-24 animate-fade-in">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-10">
              {content.logo && (
                <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-[32px] p-4 border border-slate-100 dark:border-slate-700 shadow-xl">
                  <img src={content.logo} alt="Service Logo" className="w-full h-full object-contain" />
                </div>
              )}
              <div>
                <h1 className="text-5xl lg:text-6xl font-display font-bold text-slate-900 dark:text-white leading-tight">{content.title}</h1>
                <p className="text-2xl text-primary font-medium">{content.subtitle}</p>
              </div>
            </div>
            
            <div className="mb-10 p-6 bg-slate-50 dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">Professional Fee</p>
                <p className="text-4xl font-display font-bold text-slate-900 dark:text-white">{serviceInfo?.price}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">Additional Costs</p>
                <p className="text-sm font-bold text-primary dark:text-accent-400">{serviceInfo?.fee}</p>
              </div>
            </div>

            <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">{content.description}</p>
            <button 
              onClick={handleStart} 
              className="px-10 py-5 bg-primary text-white rounded-2xl font-bold shadow-2xl shadow-primary/20 hover:scale-105 transition-all text-lg flex items-center gap-3"
            >
              Start {content.title}
              <span className="material-symbols-rounded">calendar_month</span>
            </button>
          </div>
          <div className="relative">
             <div className="absolute inset-0 bg-primary/10 blur-[100px] rounded-full -z-10 animate-pulse"></div>
            <img src={content.image} className="rounded-[50px] shadow-3xl" alt={content.title} />
          </div>
        </div>

        {/* Process */}
        <div className="mb-24">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-12 text-center">Our Process</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.steps.map((step, i) => (
              <div key={i} className="p-8 bg-slate-50 dark:bg-slate-800 rounded-3xl relative overflow-hidden group">
                <span className="text-7xl font-black text-slate-200 dark:text-slate-700 absolute top-4 right-4 opacity-50 group-hover:scale-110 group-hover:text-primary/10 transition-all duration-500">{i + 1}</span>
                <h3 className="text-xl font-bold mb-4 relative z-10">{step.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 relative z-10">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-accent/10 dark:bg-primary/10 rounded-[60px] p-8 lg:p-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-8">What We Need</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-10">To ensure a seamless strategy session, please have the following ready to discuss:</p>
              <ul className="space-y-6">
                {content.requirements.map((req, i) => (
                  <li key={i} className="flex items-center gap-5 text-slate-800 dark:text-slate-200 text-lg font-semibold">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center shrink-0">
                      <span className="material-symbols-rounded text-lg">check</span>
                    </div>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white dark:bg-slate-800 p-10 lg:p-14 rounded-[40px] shadow-2xl relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <h3 className="text-2xl font-bold mb-6">Need expert clarification?</h3>
              <p className="text-slate-500 mb-10 text-lg">Every business is unique. Our compliance partners are available for a specialized call to review your specific entity needs.</p>
              <button onClick={() => setPage('booking')} className="w-full py-5 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white font-bold rounded-2xl hover:bg-primary hover:text-white transition-all text-lg shadow-inner flex items-center justify-center gap-2">
                <span className="material-symbols-rounded">calendar_month</span>
                Book Discovery Strategy Session
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;